import type { CanvasCard, SizePreset } from "store/types";
import { DEFAULT_GRID_H } from "store/types/reports/report-generator";

import { buildChartConfig, buildKpiConfig, buildSummaryConfig, buildTableConfig } from "./cardDefaults";
import { gridWToWidthPercent } from "./gridUtils";

function uid() {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export interface Layout {
  id: string;
  label: string;
  desc: string;
  rows: number[][];
  build: () => CanvasCard[];
}

function c(
  type: CanvasCard["type"],
  gridX: number,
  gridY: number,
  gridW: number,
  gridH: number,
  config: CanvasCard["config"],
  page: number,
  preset: SizePreset | null = null
): CanvasCard {
  return {
    id: uid(),
    type,
    gridX,
    gridY,
    gridW,
    gridH,
    widthPercent: gridWToWidthPercent(gridW),
    lastPreset: preset,
    rowIndex: 0,
    orderInRow: 0,
    config,
    page
  };
}

const H = DEFAULT_GRID_H;

export const LAYOUTS: Layout[] = [
  {
    id: "executive",
    label: "Executive Summary",
    desc: "KPI row, chart, and commentary",
    rows: [[4, 4, 4], [6, 6], [12]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("chart", 0, 2, 6, H.chart, buildChartConfig(), 0, "M"),
      c("text", 6, 2, 6, H.text, { type: "text", content: "" }, 0, "M"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "header-right",
    label: "Header + Details Right",
    desc: "Summary left, KPIs right, table below",
    rows: [[8, 4], [8, 4], [12]],
    build: () => [
      c("summary", 0, 0, 8, H.summary, buildSummaryConfig(), 0),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("chart", 0, 2, 8, H.chart, buildChartConfig(), 0),
      c("kpi", 8, 2, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "header-left",
    label: "Header + Details Left",
    desc: "KPIs left, charts right, table below",
    rows: [[4, 8], [4, 8], [12]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("chart", 4, 0, 8, H.chart, buildChartConfig(), 0),
      c("kpi", 0, 4, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("chart", 4, 4, 8, H.chart, buildChartConfig(), 0),
      c("table", 0, 8, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "data-focus",
    label: "Data Focus",
    desc: "Charts with a full deal table",
    rows: [[4, 4, 4], [8, 4], [12]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("chart", 0, 2, 8, H.chart, buildChartConfig(), 0),
      c("chart", 8, 2, 4, H.chart, buildChartConfig(), 0, "S"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "narrative",
    label: "Narrative Report",
    desc: "Commentary with supporting data",
    rows: [[12], [8, 4], [12]],
    build: () => [
      c("summary", 0, 0, 12, H.summary, buildSummaryConfig(), 0, "F"),
      c("text", 0, 2, 8, H.text, { type: "text", content: "" }, 0),
      c("chart", 8, 2, 4, H.chart, buildChartConfig(), 0, "S"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "capital-account",
    label: "Capital Account Statement",
    desc: "Per-investor. Commitment, called, distributed, NAV, unfunded, MOIC.",
    rows: [[4, 4, 4], [12], [8, 4]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("table", 0, 2, 12, H.table, buildTableConfig(), 0, "F"),
      c("unfunded", 0, 7, 8, H.unfunded, { type: "unfunded" }, 0),
      c("kpi", 8, 7, 4, H.kpi, buildKpiConfig(), 0, "S")
    ]
  },
  {
    id: "portfolio-briefing",
    label: "Portfolio Briefing",
    desc: "Principal-facing. What changed since last visit, top movers, recent capital activity.",
    rows: [[4, 4, 4], [8, 4], [12]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("text", 0, 2, 8, H.text, { type: "text", content: "" }, 0),
      c("performance", 8, 2, 4, H.performance, { type: "performance", sortBy: "irr", topN: 3 }, 0, "S"),
      c("cashflow", 0, 6, 12, H.cashflow, { type: "cashflow" }, 0, "F")
    ]
  },
  {
    id: "vintage-attribution",
    label: "Vintage Year Attribution",
    desc: "IRR/MOIC grouped by vintage cohort. Investment committee use.",
    rows: [[4, 4, 4], [8, 4], [12]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("exposure", 0, 2, 8, H.exposure, { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" }, 0),
      c("performance", 8, 2, 4, H.performance, { type: "performance", sortBy: "moic", topN: 5 }, 0, "S"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "gp-manager-summary",
    label: "GP / Manager Summary",
    desc: "All deals with a given manager aggregated.",
    rows: [[12], [8, 4], [12]],
    build: () => [
      c("summary", 0, 0, 12, H.summary, buildSummaryConfig(), 0, "F"),
      c("chart", 0, 2, 8, H.chart, buildChartConfig(), 0),
      c("benchmark", 8, 2, 4, H.benchmark, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0, "S"),
      c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
    ]
  },
  {
    id: "exposure-breakdown",
    label: "Exposure Breakdown",
    desc: "Concentration by asset class, geography, strategy, vintage.",
    rows: [[8, 4], [8, 4], [4]],
    build: () => [
      c("exposure", 0, 0, 8, H.exposure, { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" }, 0),
      c("exposure", 8, 0, 4, H.exposure, { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" }, 0, "S"),
      c("exposure", 0, 4, 8, H.exposure, { type: "exposure", dimension: "Strategy", chartType: "donut", measure: "exposure" }, 0),
      c("exposure", 8, 4, 4, H.exposure, { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" }, 0, "S"),
      c("dataFreshness", 0, 8, 4, H.dataFreshness, { type: "dataFreshness" }, 0, "S")
    ]
  },
  {
    id: "quarterly-letter",
    label: "Quarterly Letter",
    desc: "Narrative commentary with supporting KPIs.",
    rows: [[12], [8, 4], [12], [12]],
    build: () => [
      c("macroBrief", 0, 0, 12, H.macroBrief, { type: "macroBrief", indicators: [] }, 0, "F"),
      c("text", 0, 3, 8, H.text, { type: "text", content: "" }, 0),
      c("chart", 8, 3, 4, H.chart, buildChartConfig(), 0, "S"),
      c("kpi", 0, 7, 12, H.kpi, buildKpiConfig(), 0, "F"),
      c("cashflow", 0, 9, 12, H.cashflow, { type: "cashflow" }, 0, "F")
    ]
  },
  {
    id: "one-pager-deal",
    label: "One-Pager Deal Summary",
    desc: "Single deal, all key characteristics and measures.",
    rows: [[4, 8], [8, 4]],
    build: () => [
      c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
      c("summary", 4, 0, 8, H.summary, buildSummaryConfig(), 0),
      c("benchmark", 0, 2, 8, H.benchmark, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0),
      c("dataFreshness", 8, 2, 4, H.dataFreshness, { type: "dataFreshness" }, 0, "S")
    ]
  },
  {
    id: "annual-report-5p",
    label: "Annual Portfolio Report",
    desc: "5-page pack: executive summary, performance, exposure, capital account, market context.",
    rows: [[4, 4, 4], [8, 4], [12]],
    build: () => {
      const p0: CanvasCard[] = [
        c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("summary", 0, 2, 8, H.summary, buildSummaryConfig(), 0),
        c("kpi", 8, 2, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("chart", 0, 4, 8, H.chart, buildChartConfig(), 0),
        c("chart", 8, 4, 4, H.chart, buildChartConfig(), 0, "S"),
        c("table", 0, 8, 12, H.table, buildTableConfig(), 0, "F")
      ];
      const p1: CanvasCard[] = [
        c("performance", 0, 0, 8, H.performance, { type: "performance", sortBy: "irr", topN: 5 }, 1),
        c("benchmark", 8, 0, 4, H.benchmark, { type: "benchmark", benchmarkIds: ["^SPX"] }, 1, "S"),
        c("cashflow", 0, 4, 12, H.cashflow, { type: "cashflow" }, 1, "F"),
        c("table", 0, 8, 12, H.table, buildTableConfig(), 1, "F")
      ];
      const p2: CanvasCard[] = [
        c("exposure", 0, 0, 6, H.exposure, { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" }, 2, "M"),
        c("exposure", 6, 0, 6, H.exposure, { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" }, 2, "M"),
        c("exposure", 0, 4, 6, H.exposure, { type: "exposure", dimension: "Strategy", chartType: "donut", measure: "exposure" }, 2, "M"),
        c("exposure", 6, 4, 6, H.exposure, { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" }, 2, "M"),
        c("unfunded", 0, 8, 8, H.unfunded, { type: "unfunded" }, 2),
        c("dataFreshness", 8, 8, 4, H.dataFreshness, { type: "dataFreshness" }, 2, "S")
      ];
      const p3: CanvasCard[] = [
        c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 3, "S"),
        c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 3, "S"),
        c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 3, "S"),
        c("table", 0, 2, 12, H.table, buildTableConfig(), 3, "F"),
        c("unfunded", 0, 7, 8, H.unfunded, { type: "unfunded" }, 3),
        c("kpi", 8, 7, 4, H.kpi, buildKpiConfig(), 3, "S")
      ];
      const p4: CanvasCard[] = [
        c("macroBrief", 0, 0, 12, H.macroBrief, { type: "macroBrief", indicators: [] }, 4, "F"),
        c("text", 0, 3, 8, H.text, { type: "text", content: "" }, 4),
        c("kpi", 8, 3, 4, H.kpi, buildKpiConfig(), 4, "S")
      ];
      return [...p0, ...p1, ...p2, ...p3, ...p4];
    }
  },
  {
    id: "investment-committee",
    label: "Investment Committee Pack",
    desc: "Fixed sequence: executive summary, deal one-pagers, exposure breakdown, macro brief.",
    rows: [[4, 4, 4], [8, 4], [12]],
    build: () => {
      const p0: CanvasCard[] = [
        c("kpi", 0, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("kpi", 4, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("kpi", 8, 0, 4, H.kpi, buildKpiConfig(), 0, "S"),
        c("performance", 0, 2, 8, H.performance, { type: "performance", sortBy: "irr", topN: 5 }, 0),
        c("benchmark", 8, 2, 4, H.benchmark, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0, "S"),
        c("table", 0, 6, 12, H.table, buildTableConfig(), 0, "F")
      ];
      const p1: CanvasCard[] = [
        c("exposure", 0, 0, 8, H.exposure, { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" }, 1),
        c("exposure", 8, 0, 4, H.exposure, { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" }, 1, "S"),
        c("cashflow", 0, 4, 8, H.cashflow, { type: "cashflow" }, 1),
        c("unfunded", 8, 4, 4, H.unfunded, { type: "unfunded" }, 1, "S")
      ];
      const p2: CanvasCard[] = [
        c("macroBrief", 0, 0, 12, H.macroBrief, { type: "macroBrief", indicators: [] }, 2, "F"),
        c("dataFreshness", 0, 3, 4, H.dataFreshness, { type: "dataFreshness" }, 2, "S")
      ];
      return [...p0, ...p1, ...p2];
    }
  }
];
